import { z } from 'zod';
import { dateOnly, digest, id, text } from './common.mjs';
import {
  nativePresentationFields,
  numericMetadataForSlot,
  checkNumericPresentation,
} from './presentation.mjs';

export const decimalSchema = z
  .object({
    kind: z.literal('decimal'),
    value: z
      .string()
      .max(128)
      .regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/),
    scale: z.number().int().min(0).max(24),
    rounding: z.enum(['half-even', 'half-up']),
    unit: text(40).nullable(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable(),
  })
  .strict()
  .refine((v) => (v.value.split('.')[1]?.length ?? 0) <= v.scale, 'Decimal exceeds declared scale');
export const scalarSchema = z.discriminatedUnion('kind', [
  decimalSchema,
  z.object({ kind: z.literal('text'), value: text() }).strict(),
  z.object({ kind: z.literal('boolean'), value: z.boolean() }).strict(),
  z.object({ kind: z.literal('date'), value: dateOnly }).strict(),
  z
    .object({ kind: z.literal('null'), reason: z.enum(['source-null', 'missing', 'redacted']) })
    .strict(),
]);
const evidenceBase = { snapshotId: id, evidenceId: id };
export const evidenceAddressSchema = z.discriminatedUnion('kind', [
  z
    .object({
      ...evidenceBase,
      kind: z.literal('dataset-cell'),
      datasetId: id,
      datasetVersion: id,
      rowId: id,
      columnId: id,
    })
    .strict(),
  z
    .object({
      ...evidenceBase,
      kind: z.literal('observation-value'),
      seriesId: id,
      observationId: id,
      columnId: id,
    })
    .strict(),
]);
export const valueSlotSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('literal'), value: scalarSchema }).strict(),
  z
    .object({
      kind: z.literal('bound'),
      bindingId: id,
      observed: scalarSchema,
      evidence: z.array(evidenceAddressSchema).min(1).max(100),
    })
    .strict(),
]);
const runBase = { id, marks: z.array(z.enum(['bold', 'italic', 'underline'])).max(3) };
export const textRunSchema = z
  .discriminatedUnion('kind', [
    z
      .object({
        ...runBase,
        ...nativePresentationFields({ colours: ['text'] }),
        kind: z.literal('text'),
        text: text(),
      })
      .strict(),
    z
      .object({
        ...runBase,
        ...nativePresentationFields({ colours: ['text'], numeric: true }),
        kind: z.literal('value'),
        slot: valueSlotSchema,
      })
      .strict(),
  ])
  .superRefine((run, ctx) => checkNumericPresentation(run, numericMetadataForSlot(run.slot), ctx));
export const modelSelectionSchema = z
  .object({
    registryId: id,
    engine: z.enum(['claude-agent-sdk', 'codex', 'native-messages']),
    providerRouteId: id,
    regionPolicy: z.literal('global'),
    selectedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export const bindingRefSchema = z.object({ id, digest }).strict();
